import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { selfRegisterApi } from '../../api/endpoints/selfRegister';
import { handleApiError } from '../../api/errorMapping';
import { ApiError } from '../../api/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { TextField } from '../ui/TextField';
import { LoadingButton } from '../ui/LoadingButton';
import { Button } from '../ui/button';
import { PasswordRulesChecklist } from './PasswordRulesChecklist';
import { buildPasswordSchema } from '../../lib/passwordRules';
import { usePasswordPolicy } from '../../hooks/usePasswordPolicy';
import { useOtpCooldown } from '../../hooks/useOtpCooldown';
import { formatTime, maskEmail, maskPhone } from '../../lib/otp';
import { identifierValueSchema } from '../../lib/identifier';

// UI DE PRUEBA (no pulida): valida el flujo de autoregistro de 3 pasos contra dev.
// Varios campos son ids técnicos (tenantId/roleId/scope/scopeRef) → inputs de texto simples.

const startSchema = z.object({
    tenantId: z.string().min(1, 'Requerido'),
    identifier: identifierValueSchema,
    roleId: z.string().min(1, 'Requerido'),
    scope: z.string().min(1, 'Requerido'),
    scopeRef: z.string().min(1, 'Requerido'),
});
type StartValues = z.infer<typeof startSchema>;

const otpSchema = z.object({
    otp: z.string().min(4, 'Ingresa el código que recibiste'),
});
type OtpValues = z.infer<typeof otpSchema>;

// La contraseña la valida la política nativa del pool Cognito; el front la refleja desde
// `GET /public/password-policy` (DEC-RBAC-052, con fallback). Mismo modelo que NewPasswordForm.
interface CompleteValues {
    password: string;
    confirmPassword: string;
}

// El completionToken es one-time y vive SOLO en el estado del componente (memoria) entre el
// paso verify-otp (donde se emite) y complete (donde se consume). No se persiste ni se muestra.
type Step =
    | { kind: 'start' }
    | { kind: 'otp'; start: StartValues }
    | { kind: 'complete'; start: StartValues; completionToken: string }
    | { kind: 'done' };

/** Muestra el mensaje mapeado del error sin romper el flujo (se queda en el paso actual). */
function showStepError(error: unknown): void {
    if (error instanceof ApiError) {
        toast.error(error.msg || 'No pudimos continuar. Revisa los datos e intenta de nuevo.');
        return;
    }
    handleApiError(error);
}

export function SelfRegisterForm() {
    const [step, setStep] = useState<Step>({ kind: 'start' });
    const { policy } = usePasswordPolicy();
    // Cooldown de reenvío refresh-proof (DEC-RBAC-056), flowKey propio. markSent() al entrar al
    // paso OTP (start OK) y con cada reenvío.
    const { secondsLeft, canResend, markSent } = useOtpCooldown('self-register');
    // Schema dinámico desde la policy del backend (DEC-RBAC-052), con fallback. Memo por policy.
    const completeSchema = useMemo(
        () =>
            z
                .object({
                    password: buildPasswordSchema(policy),
                    confirmPassword: z.string(),
                })
                .refine((d) => d.password === d.confirmPassword, {
                    message: 'Las contraseñas no coinciden',
                    path: ['confirmPassword'],
                }),
        [policy],
    );

    const startForm = useForm<StartValues>({
        resolver: zodResolver(startSchema),
        defaultValues: { tenantId: '', identifier: '', roleId: '', scope: '', scopeRef: '' },
    });
    const otpForm = useForm<OtpValues>({
        resolver: zodResolver(otpSchema),
        defaultValues: { otp: '' },
    });
    const completeForm = useForm<CompleteValues>({
        resolver: zodResolver(completeSchema),
        defaultValues: { password: '', confirmPassword: '' },
    });

    // Paso 1: start → encola el OTP por el canal del identificador (email o SMS).
    const startMutation = useMutation({
        mutationFn: (values: StartValues) =>
            selfRegisterApi.start({
                tenantId: values.tenantId,
                identifier: values.identifier,
                email: values.identifier,
                roleId: values.roleId,
                scope: values.scope,
                scopeRef: values.scopeRef,
            }),
        onSuccess: (_data, values) => {
            const canal = values.identifier.includes('@') ? 'por email' : 'por SMS';
            toast.success(`Te enviamos un código ${canal}.`);
            // Arranca el cooldown de reenvío al entrar al paso OTP (refresh-proof).
            markSent();
            setStep({ kind: 'otp', start: values });
        },
        onError: showStepError,
    });

    // Reenvío del OTP del autoregistro (DEC-RBAC-054): re-encola el código validando el `pending`
    // existente; misma postura anti-enum del start. Resetea el cooldown.
    const resendMutation = useMutation({
        mutationFn: () => {
            if (step.kind !== 'otp') throw new Error('paso inválido');
            return selfRegisterApi.resendOtp({
                tenantId: step.start.tenantId,
                identifier: step.start.identifier,
                email: step.start.identifier,
            });
        },
        onSuccess: () => {
            toast.message('Si tu invitación sigue vigente, te enviamos un código nuevo.');
            markSent();
        },
        onError: showStepError,
    });

    // Paso 2: verify-otp → emite el completionToken.
    const otpMutation = useMutation({
        mutationFn: (values: OtpValues) => {
            if (step.kind !== 'otp') throw new Error('paso inválido');
            return selfRegisterApi.verifyOtp({
                tenantId: step.start.tenantId,
                identifier: step.start.identifier,
                email: step.start.identifier,
                otp: values.otp,
            });
        },
        onSuccess: (result) => {
            if (step.kind !== 'otp') return;
            if (!result.completionToken) {
                toast.error('El servidor no devolvió el token de finalización.');
                return;
            }
            setStep({
                kind: 'complete',
                start: step.start,
                completionToken: result.completionToken,
            });
        },
        onError: showStepError,
    });

    // Paso 3: complete → provisiona el usuario ACTIVE con el completionToken.
    const completeMutation = useMutation({
        mutationFn: (values: CompleteValues) => {
            if (step.kind !== 'complete') throw new Error('paso inválido');
            // DEC-RBAC-046: ya no se manda displayName. DEC-RBAC-097: identificador dual vía puente.
            return selfRegisterApi.complete({
                tenantId: step.start.tenantId,
                identifier: step.start.identifier,
                email: step.start.identifier,
                completionToken: step.completionToken,
                password: values.password,
            });
        },
        onSuccess: () => {
            setStep({ kind: 'done' });
        },
        onError: showStepError,
    });

    return (
        <Card className="w-full max-w-md shadow-lg">
            <CardHeader className="space-y-1 text-center">
                <CardTitle className="text-2xl">Autoregistro</CardTitle>
                <CardDescription>
                    {step.kind === 'start' && 'Paso 1 de 3 — datos de la invitación'}
                    {step.kind === 'otp' && 'Paso 2 de 3 — código de verificación'}
                    {step.kind === 'complete' && 'Paso 3 de 3 — define tu cuenta'}
                    {step.kind === 'done' && 'Listo'}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {step.kind === 'start' && (
                    <form
                        onSubmit={(e) => {
                            void startForm.handleSubmit((v) => {
                                startMutation.mutate(v);
                            })(e);
                        }}
                        className="space-y-4"
                        noValidate
                    >
                        <TextField
                            label="Tenant ID"
                            error={startForm.formState.errors.tenantId?.message}
                            disabled={startMutation.isPending}
                            {...startForm.register('tenantId')}
                        />
                        <TextField
                            label="Correo o teléfono"
                            type="text"
                            autoComplete="username"
                            placeholder="tu@email.com o +52..."
                            error={startForm.formState.errors.identifier?.message}
                            disabled={startMutation.isPending}
                            {...startForm.register('identifier')}
                        />
                        <TextField
                            label="Role ID"
                            error={startForm.formState.errors.roleId?.message}
                            disabled={startMutation.isPending}
                            {...startForm.register('roleId')}
                        />
                        <TextField
                            label="Scope"
                            helperText="Ej. nivel del scope del rol"
                            error={startForm.formState.errors.scope?.message}
                            disabled={startMutation.isPending}
                            {...startForm.register('scope')}
                        />
                        <TextField
                            label="Scope Ref"
                            helperText="Id de la entidad del scope"
                            error={startForm.formState.errors.scopeRef?.message}
                            disabled={startMutation.isPending}
                            {...startForm.register('scopeRef')}
                        />
                        <LoadingButton
                            type="submit"
                            className="w-full"
                            isLoading={startMutation.isPending}
                            loadingText="Enviando…"
                        >
                            Enviar código
                        </LoadingButton>
                        <div className="text-center text-sm">
                            <Link to="/login" className="text-brand-accent hover:underline">
                                Ya tengo cuenta — iniciar sesión
                            </Link>
                        </div>
                    </form>
                )}

                {step.kind === 'otp' && (
                    <form
                        onSubmit={(e) => {
                            void otpForm.handleSubmit((v) => {
                                otpMutation.mutate(v);
                            })(e);
                        }}
                        className="space-y-4"
                        noValidate
                    >
                        <p className="text-sm text-muted-foreground">
                            Te enviamos un código a{' '}
                            <span className="font-medium">
                                {step.start.identifier.includes('@')
                                    ? maskEmail(step.start.identifier)
                                    : maskPhone(step.start.identifier)}
                            </span>
                            .
                        </p>
                        <TextField
                            label="Código (OTP)"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="123456"
                            error={otpForm.formState.errors.otp?.message}
                            disabled={otpMutation.isPending}
                            {...otpForm.register('otp')}
                        />
                        <LoadingButton
                            type="submit"
                            className="w-full"
                            isLoading={otpMutation.isPending}
                            loadingText="Verificando…"
                        >
                            Verificar
                        </LoadingButton>

                        {/* Reenviar OTP (DEC-RBAC-054) con cooldown refresh-proof. */}
                        <div className="text-center text-sm space-y-1">
                            {secondsLeft > 0 ? (
                                <p className="text-muted-foreground">
                                    Puedes pedir un código nuevo en{' '}
                                    <span className="font-mono font-semibold">
                                        {formatTime(secondsLeft)}
                                    </span>
                                </p>
                            ) : (
                                <p className="text-muted-foreground">¿No te llegó el código?</p>
                            )}
                            <Button
                                type="button"
                                variant="ghost"
                                className="w-full text-sm hover:bg-muted hover:text-foreground"
                                disabled={!canResend || resendMutation.isPending}
                                onClick={() => {
                                    resendMutation.mutate();
                                }}
                            >
                                Reenviar OTP
                            </Button>
                        </div>

                        {/* "Atrás" (vuelve a los datos de la invitación) + "Volver al login". */}
                        <Button
                            type="button"
                            variant="ghost"
                            className="w-full text-sm hover:bg-muted hover:text-foreground"
                            onClick={() => {
                                otpForm.reset();
                                setStep({ kind: 'start' });
                            }}
                        >
                            <ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
                            Volver
                        </Button>
                        <div className="text-center text-sm">
                            <Link to="/login" className="text-brand-accent hover:underline">
                                Volver al login
                            </Link>
                        </div>
                    </form>
                )}

                {step.kind === 'complete' && (
                    <form
                        onSubmit={(e) => {
                            void completeForm.handleSubmit((v) => {
                                completeMutation.mutate(v);
                            })(e);
                        }}
                        className="space-y-4"
                        noValidate
                    >
                        <p className="text-sm text-muted-foreground">
                            Define tu contraseña para{' '}
                            <span className="font-medium">
                                {step.start.identifier.includes('@')
                                    ? maskEmail(step.start.identifier)
                                    : maskPhone(step.start.identifier)}
                            </span>
                            .
                        </p>
                        <TextField
                            label="Contraseña"
                            type="password"
                            autoComplete="new-password"
                            error={completeForm.formState.errors.password?.message}
                            disabled={completeMutation.isPending}
                            {...completeForm.register('password')}
                        />
                        <PasswordRulesChecklist
                            value={completeForm.watch('password')}
                            policy={policy}
                        />
                        <TextField
                            label="Confirmar contraseña"
                            type="password"
                            autoComplete="new-password"
                            error={completeForm.formState.errors.confirmPassword?.message}
                            disabled={completeMutation.isPending}
                            {...completeForm.register('confirmPassword')}
                        />
                        <LoadingButton
                            type="submit"
                            className="w-full"
                            isLoading={completeMutation.isPending}
                            loadingText="Creando cuenta…"
                        >
                            Crear cuenta
                        </LoadingButton>
                        <div className="text-center text-sm">
                            <Link to="/login" className="text-brand-accent hover:underline">
                                Volver al login
                            </Link>
                        </div>
                    </form>
                )}

                {step.kind === 'done' && (
                    <div className="space-y-4 text-center">
                        <p className="text-sm">Tu cuenta fue creada. Ya puedes iniciar sesión.</p>
                        <Link to="/login">
                            <Button type="button" className="w-full">
                                Ir a iniciar sesión
                            </Button>
                        </Link>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
